import { IconButton } from '@mui/material';
import React, { MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import { CustomTooltip } from 'src/components/tooltip/CustomTooltip';
import { PATHS } from 'src/constants/paths';
import {
  KeyboardArrowDownIconStyled,
  KeyboardArrowUpIconStyled,
} from 'src/features/locations/scheduleLocationGroupPage/modules/LocationSchedulePageTable/LocationSchedulePageTable.styled';

import { chooseTag, columns } from '../../BulkReplaceReviewPage.helper';
import { REVIEW_PAGE_COLUMN } from '../../BulkReplaceReviewPage.interface';
import {
  Chip,
  CustomCheckbox,
  CustomTableCell,
  LinkContainer,
  TableRowContainer,
  VariationChip,
} from '../../BulkReplaceReviewPage.styled';
import { getLocationGroupCountText } from './BulkReplaceReviewTableRow.helper';
import { AssemblyBasicTableRowProps } from './BulkReplaceReviewTableRow.interface';

const RowHeader = ({
  assemblyName,
  assemblyId,
}: {
  assemblyName: string;
  assemblyId: number;
}) => {
  return (
    <CustomTooltip
      width={500}
      placement="right"
      title={assemblyName}
      overflowWidth={334}
      renderChildren={(ref, style) => (
        <LinkContainer variant="text">
          <Link
            to={`${PATHS.ASSEMBLY_DETAILS(assemblyId)}`}
            target="_blank"
            ref={ref}
            style={style}>
            {assemblyName}
          </Link>
        </LinkContainer>
      )}
    />
  );
};

export const BulkReplaceReviewTableRow: React.FC<
  AssemblyBasicTableRowProps
> = ({
  expendAssemblies,
  assembly,
  allowedAssemblyIds,
  selectedAssemblies,
  isChildRow,
  isInherited,
  isParentChecked,
  onExpendedAssemblies,
  onSelectedAssemblies,
  defaultAssemblies,
  stagedAssembly,
}) => {
  const {
    id: assemblyId,
    name: assemblyName,
    layout: assemblyChannelType,
    childrenAssemblies,
  } = assembly;

  const onHandleClickAssemblyRow = (
    event: MouseEvent<HTMLElement>,
    id: number,
  ) => {
    event.stopPropagation();
    onExpendedAssemblies(id);
  };

  const locationGroupCountText = getLocationGroupCountText(
    defaultAssemblies,
    assemblyId,
  );

  const isInAllowedAssemblyIds = allowedAssemblyIds?.includes(assemblyId);

  return (
    <TableRowContainer>
      {columns.map((column, key) => (
        <CustomTableCell
          key={key}
          padding={REVIEW_PAGE_COLUMN.CHECK_BOX === column.key && 'checkbox'}
          align={REVIEW_PAGE_COLUMN.TAGS === column.key ? 'right' : 'left'}
          width={column.width}>
          {(() => {
            switch (column.key) {
              case REVIEW_PAGE_COLUMN.CHECK_BOX:
                return (
                  <CustomCheckbox
                    color="primary"
                    checked={selectedAssemblies.includes(assemblyId)}
                    disabled={
                      isChildRow
                        ? isInherited
                          ? true
                          : !isInAllowedAssemblyIds ||
                            stagedAssembly?.assembly_id
                        : !isInAllowedAssemblyIds || stagedAssembly?.assembly_id
                    }
                    indeterminate={
                      isChildRow
                        ? isInherited
                          ? true
                          : !isInAllowedAssemblyIds
                        : !isInAllowedAssemblyIds
                    }
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      onSelectedAssemblies(event.target.checked, assemblyId)
                    }
                  />
                );
              case REVIEW_PAGE_COLUMN.ASSEMBLY_NAME:
                return (
                  <RowHeader
                    assemblyId={assemblyId}
                    assemblyName={assemblyName}
                  />
                );
              case REVIEW_PAGE_COLUMN.CHANNEL_TYPE:
                return <>{assemblyChannelType?.layoutType?.name}</>;
              case REVIEW_PAGE_COLUMN.NO_OF_LOCATION_GROUPS:
                return <>{locationGroupCountText}</>;
              case REVIEW_PAGE_COLUMN.STATE:
                return <></>;
              case REVIEW_PAGE_COLUMN.TAGS:
                return isChildRow ? (
                  <VariationChip
                    enabled={
                      !!isInAllowedAssemblyIds && !stagedAssembly?.assembly_id
                    }
                    checked={selectedAssemblies.includes(assemblyId)}
                    isChild={isChildRow}
                    isInherited={isInherited}
                    isParentChecked={isParentChecked}
                    label={chooseTag(
                      !!isInAllowedAssemblyIds,
                      isChildRow,
                      selectedAssemblies.includes(assemblyId),
                      !!isInherited,
                      stagedAssembly,
                      isParentChecked,
                    )}
                  />
                ) : (
                  <Chip
                    enabled={
                      !!isInAllowedAssemblyIds && !stagedAssembly?.assembly_id
                    }
                    checked={selectedAssemblies.includes(assemblyId)}
                    label={chooseTag(
                      !!isInAllowedAssemblyIds,
                      isChildRow,
                      selectedAssemblies.includes(assemblyId),
                      !!isInherited,
                      stagedAssembly,
                      isParentChecked,
                    )}
                  />
                );
              case REVIEW_PAGE_COLUMN.TOGGLE:
                return childrenAssemblies.length ? (
                  <IconButton
                    aria-label="expand row"
                    size="small"
                    onClick={(event) =>
                      onHandleClickAssemblyRow(event, assemblyId)
                    }>
                    {expendAssemblies.includes(assemblyId) ? (
                      <KeyboardArrowUpIconStyled />
                    ) : (
                      <KeyboardArrowDownIconStyled />
                    )}
                  </IconButton>
                ) : null;
              default:
                return null;
            }
          })()}
        </CustomTableCell>
      ))}
    </TableRowContainer>
  );
};
